import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';
import { NEXT_PUBLIC_GTM_DISABLED } from '@/lib/env';
import logger from './logger';

const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_CONTAINER_ID;

// First-party cookies set by GTM-fired tags once gtm.js runs:
// - GA4 / Google Ads: _ga, _ga_<container>, _gid, _gat*, _gac_*, and the Ads linker _gcl_*
// - Reddit Pixel (a GTM community-template tag, first-party on our domain): _rdt_uuid, _rdt_em
// Every name starts with one of these prefixes; cleared on teardown so no analytics identifiers
// survive a withdrawal. The DoubleClick `test_cookie` is third-party (.doubleclick.net) and cannot
// be removed by first-party JS, so it is intentionally not listed (it expires on its own).
const GTM_COOKIE_PREFIXES = ['_ga', '_gid', '_gat', '_gac', '_gcl', '_rdt'];

// Mirrors the persisted consent decision: true once GTM is allowed to run, flipped back to false on Reject/withdraw. Gates dataLayer pushes (see dataLayer.ts) so no events are queued pre-consent or after withdrawal, complementing the consent-gated <Script> render. The consent store hydrates on import, so the handler replay sets this before component mount effects run.
let enabled = false;

/** GTM is configured for this site (container id present) and not env-disabled. */
export const isGtmConfigured = (): boolean =>
  Boolean(GTM_CONTAINER_ID) && !NEXT_PUBLIC_GTM_DISABLED;

export const isGtmEnabled = (): boolean => enabled;

export const getGtmContainerId = (): string | undefined => GTM_CONTAINER_ID;

export const getGtmScriptSrc = (): string | null =>
  isGtmConfigured() ? `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}` : null;

/**
 * Whether the `<noscript>` GTM iframe may be rendered server-side. The runtime `enabled` flag is client-only (set by the consent handler), so the no-JS path is decided from the request: render only when GTM is configured AND consent allows it (off-GDPR, or the persisted cookie is accepted).
 * Fail-closed when the decision is unknown (e.g. statically rendered GDPR pages with no request).
 */
export const isGtmNoscriptAllowed = (consentAccepted: boolean, isGdprRegion: boolean): boolean =>
  isGtmConfigured() && (!isGdprRegion || consentAccepted);

const isGtmCookie = (name: string): boolean =>
  GTM_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix));

// The Google Ads linker also persists to localStorage (e.g. `_gcl_ls`), not just `_gcl*` cookies.
const GTM_LOCAL_STORAGE_PREFIXES = ['_gcl'];

const isGtmLocalStorageKey = (key: string): boolean =>
  GTM_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));

const clearGtmStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const cookiesRemoved = clearBrowserCookies(isGtmCookie);
  const localRemoved = clearWebStorage(() => window.localStorage, isGtmLocalStorageKey);

  // Drop any events staged in the in-memory dataLayer so nothing queued pre-/post-consent is replayed if GTM is re-enabled later in the same page session.
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.length = 0;
  }

  logger.debug('Cleared GTM storage', { cookiesRemoved, localRemoved });
};

/** Initialise the dataLayer and emit the gtm.js bootstrap event the CDN script consumes on load. */
const initDataLayer = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
};

/** Allow GTM to run (dataLayer pushes + gtm.js script). No-op when not configured / on the server. */
export const enableGtm = (): boolean => {
  if (typeof window === 'undefined' || !isGtmConfigured()) {
    enabled = false;
    return false;
  }

  if (!enabled) {
    logger.debug('GTM enabled: dataLayer init and gtm.js allowed');
    initDataLayer();
  }
  enabled = true;
  return true;
};

/**
 * Stop GTM and clear its client-side storage (Reject/withdraw teardown).
 *
 * An already-loaded gtm.js runtime cannot be fully unloaded without a reload, so teardown means: stop our dataLayer pushes (via `enabled`), drop the staged dataLayer, and remove GTM/GA cookies so collection genuinely stops rather than only halting new events.
 */
export const disableGtm = (): void => {
  if (enabled) {
    logger.debug('GTM disabled: stopping dataLayer pushes and clearing storage');
  }
  enabled = false;
  clearGtmStorage();
};
